'use client';

import clsx from 'clsx';
import Link from 'next/link';
import { GTM_EVENT, GTM_MANUAL_PUSH_ATTR } from '@/shared/constants/gtm';
import { gtmClick, pushGTMClickFromElement } from '@/shared/utils/gtm';

import { getEventStatus, type HubEvent } from '../data';
import { getHubImages, HUB_IMAGE_FALLBACK, resolveHubEventImageSrc } from '../_lib/hubEventImage';
import { useImageFallback } from '../_lib/useImageFallback';
import { SUB_LABEL } from '../_lib/tabs';
import s from '../page.module.scss';
import { HubEventPeriodText } from './HubEventPeriodText';
import { HubEventStatusBadges } from './HubEventStatusBadges';

interface HubSlideItemProps {
  event: HubEvent;
}

export function HubSlideItem({ event }: HubSlideItemProps) {
  const status = getEventStatus(event);
  const images = getHubImages(event.imageKey);
  const banner = useImageFallback(
    resolveHubEventImageSrc(images.banner),
    HUB_IMAGE_FALLBACK.banner,
  );
  const bannerMo = useImageFallback(
    resolveHubEventImageSrc(images.bannerMo),
    HUB_IMAGE_FALLBACK.bannerMo,
  );

  return (
    <div
      className={s.slide_item}
      style={{
        '--slide-bg-pc': `url(${banner})`,
        '--slide-bg-mo': `url(${bannerMo})`,
      } as React.CSSProperties}
    >
      <Link
        href={event.href}
        target={event.target}
        rel={event.target === '_blank' ? 'noopener noreferrer' : undefined}
        className={clsx(s.slide_inner, event.slideColor === 'black' && s.slide_inner_color_black)}
        {...gtmClick(
          GTM_EVENT.BANNER,
          event.cardTitle ?? SUB_LABEL[event.category],
          event.href,
        )}
        {...{ [GTM_MANUAL_PUSH_ATTR]: '' }}
        onClick={(e) => pushGTMClickFromElement(e.currentTarget)}
      >
        <div className={s.slide_content}>
          {status && (
            <HubEventStatusBadges
              status={status}
              subLabel={SUB_LABEL[event.category]}
            />
          )}
          <h2 className={s.slide_title}>{event.slideTitle}</h2>
          <div className={s.slide_meta}>
            <HubEventPeriodText status={status} className={s.slide_period} />
            {event.memo && <span className={s.slide_memo}>{event.memo}</span>}
          </div>
        </div>
      </Link>
    </div>
  );
}
